import { ICustomZShapeType } from "../../../responses/customPanelTypes";
import { ICornerSelectionAimGeometryMaterials } from "../geometry/cornerSelectionAimGeometryMaterials";
import { ViewerOverlayZShapeSelectorGeometry } from "../geometry/viewerOverlayZShapeSelectorGeometry";
import { ZShapeCorner } from "../panels/z-shape/zShapeCorner";
import { placePanelToolCursor, placeZShapePanelToolCursor } from "./toolCursors";
import { ToolInterface } from "./toolInterface";
import eventBus from "../eventBus/eventDispatcher";

export const newCustomZShapePanelToolName = "dextall-editor-custom-z-shaped-panel-placement-tool";
const overlayName = "dextall-editor-new-custom-z-shape-panel-overlay";

export class CustomZShapedPanelsPlacementTool extends ToolInterface {
    private readonly cornerSelectors: ViewerOverlayZShapeSelectorGeometry[] = [];
    private viewer: Autodesk.Viewing.Viewer3D | null = null;
    private selectedCustomType: ICustomZShapeType | null = null;
    private activeCorner: ViewerOverlayZShapeSelectorGeometry | null = null;
    private suspended = false;

    constructor() {
        super();

        this.names = [newCustomZShapePanelToolName];
    }

    debugCornerDirections = false;

    setCorners(corners: ZShapeCorner[], customPanelType: ICustomZShapeType) {
        if (this.viewer === null) {
            throw new Error("Invalid state! The tool MUST be activated before setting the corners!");
        }

        this.selectedCustomType = customPanelType;

        this.cornerSelectors.length = 0;

        const materials = this.createCornerAimMaterials();

        for (const corner of corners) {
            for (let i = 0; i < corner.wallCorner.heights.length - 1; ++i) {
                const cornerSelectionGeometry = new ViewerOverlayZShapeSelectorGeometry(
                    corner,
                    i,
                    this.viewer,
                    materials,
                    overlayName,
                    this.debugCornerDirections,
                );

                cornerSelectionGeometry.draw();

                this.cornerSelectors.push(cornerSelectionGeometry);
            }
        }
    }

    activate(_name: string, viewer: Autodesk.Viewing.Viewer3D) {
        this.viewer = viewer;
        this.viewer.impl.createOverlayScene(overlayName);
        this.viewer.select([]);
        this.suspended = false;
        this.activeCorner = null;
    }

    deactivate() {
        this.viewer?.impl.removeOverlayScene(overlayName);
        this.viewer = null;
        this.selectedCustomType = null;
        this.cornerSelectors.length = 0;
        this.activeCorner = null;
    }

    getPriority() {
        return 42; // Use any number higher than 0 (the priority of all default tools)
    }

    getCursor(): string | null {
        return this.activeCorner ? placePanelToolCursor : placeZShapePanelToolCursor;
    }

    handleButtonDown(_event: MouseEvent, _button: number) {
        this.suspended = true;
        return false;
    }

    handleButtonUp(_event: MouseEvent, _button: number) {
        this.suspended = false;
        return false;
    }

    handleMouseMove(event: MouseEvent) {
        if (this.suspended) {
            return false;
        }

        const newActiveCorner = this.findCorner(event.canvasX, event.canvasY);

        if (newActiveCorner === this.activeCorner || (newActiveCorner === null && this.activeCorner === null)) {
            return true;
        }

        if (this.activeCorner) {
            this.activeCorner.setSelected(false);
        }

        if (newActiveCorner) {
            newActiveCorner.setSelected(true);

            this.activeCorner = newActiveCorner;
        } else {
            this.activeCorner = null;
        }

        this.viewer?.impl.invalidate(false, false, true);

        return true;
    }

    handleSingleClick(event: MouseEvent, button: number): boolean {
        if (!(this.activeCorner && this.selectedCustomType && button === 0)) {
            return true;
        }

        const corner = this.activeCorner.corner;

        eventBus.dispatchEvent({
            type: "Dextall.CustomZShapedPanels.CreateNew",
            payload: {
                wallCornerId: corner.wallCorner.id,
                shelfEndWallCornerId: corner.shelfEndCorner.id,
                customZShapedPanelTypeId: this.selectedCustomType.id,
                heightIndex: this.activeCorner.heightIndex,
                offset: 0,
                single: !event.ctrlKey,
            },
        });

        return true;
    }

    handleKeyDown(event: KeyboardEvent, _keyCode: number) {
        return event.code === "ControlLeft" || event.code === "ControlRight";
    }

    private findCorner(clientX: number, clientY: number): ViewerOverlayZShapeSelectorGeometry | null {
        if (!this.viewer) {
            throw new Error("Invalid state");
        }

        const viewportVector = this.viewer.impl.clientToViewport(clientX, clientY);

        const ray = new THREE.Ray();

        this.viewer.impl.viewportToRay(viewportVector, ray);

        return this.cornerSelectors.find(x => x.intersects(ray)) || null;
    }

    private createCornerAimMaterials(): ICornerSelectionAimGeometryMaterials {
        if (!this.viewer) {
            throw new Error("Invalid state");
        }

        const presetIndex = Math.max(0, this.viewer.impl.currentLightPreset());
        const bgColor = Autodesk.Viewing.Private.LightPresets[presetIndex].bgColorGradient;
        // TODO: these calculations can lead to float colors, which are ignored by three.js and instead interpreted as white
        // In r125 the float colors generate an "Unknown color" warning
        const lineColor = "rgb(" + (255 - bgColor[0]) + "," + (255 - bgColor[1]) + "," + (255 - bgColor[2]) + ")";

        return {
            cornerPointMaterial: new THREE.MeshBasicMaterial({ color: 0x0000ff }),
            selectedCornerPointMaterial: new THREE.MeshBasicMaterial({ color: 0xff0000 }),
            planeMaterial: new THREE.MeshBasicMaterial({
                opacity: 0,
                color: 0x287EEA,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false,
                transparent: true,
            }),
            lineMaterial: new THREE.LineBasicMaterial({
                color: lineColor,
                linewidth: 1,
                depthTest: false,
                depthWrite: false,
                transparent: true,
            }),
        };
    }
}
